import React, { useState } from 'react';
import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { styled, useTheme } from '@mui/material/styles';
import NextLink from 'next/link';

import { SimpleCrudListFilters } from '../SimpleCrudUtils';
import EmptyContent from '@common/components/EmptyContent';
import useStudentList from '@common/hooks/useStudentList';
import { trpc } from '@common/utils/trpcAmplify';
import { PATH_OVERRIDE } from '../../routes/paths';

// ----------------------------------------------------------------------

const BaseCell = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
}));

const columnMetadata = [
  { headerName: 'Student', field: 'full_id' },
  { headerName: 'Date', field: 'learning_session_date' },
  { headerName: 'Subject', field: 'subject' },
  { headerName: '', field: 'id' },
];

export default function OverrideList() {
  const theme = useTheme();
  const studentList = useStudentList(true);
  const [filters, setFilters] = useState<string>('{}');

  const outgoingFilters = JSON.parse(filters);
  const { data: listResults, isLoading } = trpc.override.list.useQuery(outgoingFilters, {
    enabled: Object.keys(outgoingFilters).length > 0, // wait for initial filter setup
  });

  const overrideData = listResults?.results;
  const total = listResults?.total;

  const columns: GridColDef[] = columnMetadata.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    flex: 1, // required for even spacing
    sortable: true,
    resizable: false,
    headerAlign: 'left',
    disableReorder: true,
    renderCell: (params) => {
      let value = params.value;
      if (column.field === 'full_id') {
        value = studentList?.find((student) => student.student_id === value)?.name;
      }

      if (column.field === 'learning_session_date') {
        value = params.value;
      }

      if (column.field === 'id') {
        value = (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-around' }}>
            <NextLink href={`${PATH_OVERRIDE.view}?id=${params.value}`} passHref>
              <Button variant="contained">View</Button>
            </NextLink>
            <NextLink href={`${PATH_OVERRIDE.edit}?id=${params.value}`} passHref>
              <Button variant="contained">Edit</Button>
            </NextLink>
          </Box>
        );
      }

      if (!React.isValidElement(value)) {
        value = <Typography fontWeight={'600'}>{value}</Typography>;
      }

      return (
        <BaseCell
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(65, 184, 212, 0.12)',
            padding: '12px 18px',
          }}
        >
          {value}
        </BaseCell>
      );
    },
  }));

  return (
    <TableContainer
      component={Paper}
      sx={{
        paddingY: '24px',
      }}
    >
      <SimpleCrudListFilters
        onSubmit={setFilters}
        total={total as number}
        showQC={false}
        overrideCampusFilter={true}
      />
      <DataGridPro
        autoHeight={true}
        rows={overrideData || []}
        getRowHeight={() => 'auto'}
        columns={columns}
        loading={!studentList || isLoading}
        disableColumnMenu={true}
        rowSelection={false}
        onCellClick={() => {}}
        hideFooter
        slots={{
          noRowsOverlay: () => (
            <EmptyContent
              title="Oops!"
              description="No override data found for those filters."
              sx={{
                '& span.MuiBox-root': { height: 160 },
              }}
              img="/assets/illustrations/illustration_empty_content_custom.png"
            />
          ),
        }}
        sx={{
          borderRadius: '8px',
          '.MuiDataGrid-columnHeader': {
            paddingLeft: '18px',
            backgroundColor: '#F9FAFB',
            color: theme.palette.text.secondary,
            ['&:focus']: {
              outline: 'none',
            },
          },
          '.MuiDataGrid-cell': {
            padding: 0,
          },
          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />
    </TableContainer>
  );
}
